// GET /api/jobs/{job_id}, per the M1 execution plan §5.1 (owner WS-C). Used to
// poll the first-sync job started by the exchange. Plaid backfills history
// page by page -- there is no total to show -- so `progress` is whatever
// shape the job writes (e.g. a page/added count) and is rendered as an
// indeterminate-with-a-count bar, never a fabricated percentage (execution
// plan WS-3 step 3).
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

export const jobStatusSchema = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);

export const jobSchema = z.object({
  job_id: z.string(),
  kind: z.string(),
  params: z.record(z.string(), z.unknown()),
  status: jobStatusSchema,
  progress: z.record(z.string(), z.unknown()),
  cursor: z.string().nullable(),
  batch_id: z.string().nullable(),
  storage_key: z.string().nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
});

export type JobStatus = z.infer<typeof jobStatusSchema>;
export type Job = z.infer<typeof jobSchema>;

const SETTLED_STATUSES: JobStatus[] = ['done', 'failed', 'cancelled'];

/**
 * ENG-F-004: a confirmed 404/`not_found` job will never settle -- polling it
 * forever burns a request every 2s per dead id. Distinct from every other
 * fetch failure (network blip, 500), which keeps its retry/backoff behavior
 * unchanged.
 */
function isNotFoundJobError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status === 404) return true;
  const payload = error.response?.data;
  return isErrorEnvelope(payload) && payload.error.code === 'not_found';
}

/** Polls while the job is queued/running; stops once it settles or is confirmed gone (404/`not_found`). `jobId` may be null while unknown. */
export function useJob(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ['jobs', jobId],
    enabled: Boolean(jobId),
    queryFn: async (): Promise<Job> => {
      const response = await client.get(`/api/jobs/${jobId}`);
      return parseEnvelope(jobSchema, response.data).data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status) return SETTLED_STATUSES.includes(status) ? false : 2000;
      if (isNotFoundJobError(query.state.error)) return false;
      return 2000;
    },
  });
}
